import { useSelector } from 'react-redux';
import { createCachedSelector } from 're-reselect';
import { selectCloudName, selectNewTlsDomain } from 'root/selectors';
import { MEDIA_TYPES } from './data/consts';
import { selectFilterParams } from './selectors';

const selectSearchParams = (state, source) => ({
  searchPhrase: state[source].searchPhrase,
  mediaType: state[source].mediaType,
  sortOrder: state[source].sortOrder,
});
const memoizedSelectSearchParams = createCachedSelector([selectSearchParams], (props) => ({ ...props }))((state, source) => source);
export const useSearchParams = (source) => useSelector((state) => memoizedSelectSearchParams(state, source));

const selectUI = (state, source) => ({
  isFiltersPanelOpen: state[source].isFiltersPanelOpen,
  areFiltersSet: state[source].areFiltersSet,
  purchaseInProgress: state[source].purchaseInProgress,
});
const memoizedSelectUI = createCachedSelector([selectUI], (props) => ({ ...props }))((state, source) => source);
export const useUI = (source) => useSelector((state) => memoizedSelectUI(state, source));

export const selectHomePageAssets = (state, source) => ({
  items: state[source].homePageItems,
  assetsInProgress: state[source].list.assetsInProgress,
  selected: state[source].list.selected,
});
const memoizedSelectHomePageAssets = createCachedSelector([selectHomePageAssets], (props) => ({ ...props }))((state, source) => source);
export const useHomePageAssets = (source) => useSelector((state) => memoizedSelectHomePageAssets(state, source));

export const useCloudName = (source) => useSelector(selectCloudName);

export const useNewTlsDomain = (source) => useSelector(selectNewTlsDomain);

const selectGettyServer = (state) => state.widgetConfig.gettyServer;
export const useGettyServer = () => useSelector(selectGettyServer);

export const selectSelectedAsset = (state, source) => ({
  selectedFormat: state[source].selectedFormat,
  selectedAsset:
    state[source].list.items.find((a) => a.id === state[source].list.selected) ??
    state[source].homePageItems.find((a) => a.id === state[source].list.selected),
  purchasedUrl: state[source].purchasedUrl,
});
const memoizedSelectSelectedAsset = createCachedSelector([selectSelectedAsset], (props) => ({ ...props }))((state, source) => source);
export const useSelectedAsset = (source) => useSelector((state) => memoizedSelectSelectedAsset(state, source));

export const selectProducts = (state, source) => ({
  products: state[source].products,
  selectedProduct: state[source].selectedProduct,
});
const memoizedSelectProducts = createCachedSelector([selectProducts], (props) => ({ ...props }))((state, source) => source);
export const useProducts = (source) => useSelector((state) => memoizedSelectProducts(state, source));

const selectPurchaseInfo = (state, source) => {
  const { selectedAsset } = selectSelectedAsset(state, source);

  const isAssetIncludedInProduct = selectedAsset?.productTypes.includes(state[source].selectedProduct?.type);
  const canPurchase = Boolean(state[source]?.selectedProduct && !state[source].selectedProduct?.exceeded && state[source].selectedFormat);
  const isAlreadyPurchased = state[source].downloads
    .filter((item) => selectedAsset?.mediaType.includes(item.mediaType))
    .map((item) => item.id)
    .includes(selectedAsset?.id);
  return {
    isAlreadyPurchased,
    canPurchase: canPurchase && isAssetIncludedInProduct,
    isAssetIncludedInProduct,
    canUpload: Boolean((canPurchase || (isAlreadyPurchased && state[source].selectedFormat)) && state[source]?.purchasedUrl),
  };
};
const memoizedSelectPurchaseInfo = createCachedSelector([selectPurchaseInfo], (props) => ({ ...props }))((state, source) => source);
export const usePurchaseInfo = (source) => useSelector((state) => memoizedSelectPurchaseInfo(state, source));

export const selectDownloadedAssets = (state, source) => ({
  downloads: state[source].downloads,
  shouldDownloadsBeRefreshed: state[source].shouldDownloadsBeRefreshed,
});

const selectAccountInfo = (state, source) => ({
  userName: [state[source].account.firstName, state[source].account.lastName].join(' '),
});
const memoizedSelectAccountInfo = createCachedSelector([selectAccountInfo], (props) => ({ ...props }))((state, source) => source);
export const useAccountInfo = (source) => useSelector((state) => memoizedSelectAccountInfo(state, source));

export const selectDisabledFilterParams = (state, source) => {
  const filters = selectFilterParams(state, source);
  const searchParams = selectSearchParams(state, source);
  const filtersState = {};

  Object.keys(filters).map((f) => (filtersState[f] = false));

  if (filters.licenseType !== 'creative') {
    filtersState.excludeEditorialUseOnly = true;
    filtersState.embedContentOnly = true;
  }
  if (filters.licenseType === 'all' && searchParams.mediaType === MEDIA_TYPES.VIDEOS) {
    filtersState.licenseModels = true;
    filtersState.frameRates = true;
    filtersState.videoCompositions = true;
    filtersState.viewpoints = true;
    filtersState.imageTechniques = true;
  }
  if (filters.licenseType === 'editorial' && searchParams.mediaType === MEDIA_TYPES.VIDEOS) {
    filtersState.licenseModels = true;
  }

  return filtersState;
};
const memoizedSelectDisabledFilterParams = createCachedSelector([selectDisabledFilterParams], (props) => ({ ...props }))((state, source) => source);
export const useDisabledFilterParams = (source) => useSelector((state) => memoizedSelectDisabledFilterParams(state, source));
